import React, { useState, useCallback, useEffect } from "react";
import {
  Container,
  Paper,
  Typography,
  Button,
  Switch,
  FormControlLabel,
  Box,
  TextField,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import * as Api from "../service";
import useStore from "../context";
import { useTranslation } from "localization/translation";

const initialData = {
  is2FAEnabled: false,
  qrImage: null,
  secret: null,
};

export const ProfileSecuritySettings = () => {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(true);
  const [isUpdating, setIsUpdating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const [dialogInput, setDialogInput] = useState(""); 
  const [dialogAction, setDialogAction] = useState(null);

  const { setErrorMsg } = useStore();
  const translation = useTranslation();

  const fetchSecurityData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await Api.main.getSecuritySettings(); 
      setData(res);
    } catch (e) {
      setErrorMsg(e.message || translation.err_fetch_settings);
    } finally {
      setLoading(false);
    }
  }, [setErrorMsg, translation]);

  useEffect(() => {
    fetchSecurityData();
  }, [fetchSecurityData]);

  const handleToggleChange = (event) => {
    const isChecked = event.target.checked;
    if (isChecked) {
      setDialogAction('GENERATE');
      setDialogInput('');
      setDialogOpen(true);
    } else {
      setDialogAction('DISABLE');
      setDialogInput('');
      setDialogOpen(true);
    }
  };

  const handleGenerateSecret = useCallback(async (password) => {
    setIsUpdating(true);
    try {
      const res = await Api.main.generate2FASecret({ password });
      setData(prev => ({ 
        ...prev, 
        ...res,
        is2FAEnabled: true,
      }));
      setErrorMsg(translation.secret_updated_success);
      setDialogOpen(false);
      setDialogInput("");
    } catch (e) {
      setErrorMsg(e.message || translation.err_generate_secret);
    } finally {
      setIsUpdating(false);
    }
  }, [setErrorMsg, translation]);

  const handleDialogSubmit = useCallback(async () => {
    setIsUpdating(true);
    
    try {
      if (dialogAction === 'DISABLE') {
        if (dialogInput.length !== 6) {
          setErrorMsg(translation.err_code_length);
          return;
        }

        await Api.main.disable2FA({ code: dialogInput });
        setData({ 
          ...initialData, 
          is2FAEnabled: false, 
          qrImage: null, 
          secret: null 
        });
        setErrorMsg(translation.disabled_success);
        setDialogOpen(false);
        setDialogInput("");
      } else if (dialogAction === 'GENERATE') {
        if (!dialogInput) {
          setErrorMsg(translation.password_required);
          return;
        }
        await handleGenerateSecret(dialogInput);
      }
    } catch (e) {
      const errorKey = dialogAction === 'DISABLE' 
        ? translation.err_two_fa_invalid_expired 
        : translation.err_password_invalid; 

      setErrorMsg(e.message || errorKey); 
    } finally {
      setIsUpdating(false);
    }
  }, [dialogInput, dialogAction, setErrorMsg, translation, handleGenerateSecret]);

  const handleOpenDialog = (type) => {
    setDialogAction(type);
    setDialogInput('');
    setDialogOpen(true);
  }

  if (loading) {
    return (
      <Container className="pt-10 text-center">
        <CircularProgress />
        <Typography>{translation.loading_settings}</Typography>
      </Container>
    );
  }

  const getDialogDetails = () => {
    if (dialogAction === 'DISABLE') {
      return {
        title: translation.disable_two_fa_title,
        prompt: translation.disable_two_fa_prompt,
        label: translation.code_two_fa_label,
        type: 'text',
        maxLength: 6,
        confirmLabel: translation.confirm_disable,
        isValid: dialogInput.length === 6,
      };
    } else if (dialogAction === 'GENERATE') {
      return {
        title: translation.confirm_action_password_title,
        prompt: translation.confirm_action_password_prompt,
        label: translation.password,
        type: 'password',
        maxLength: 50,
        confirmLabel: translation.confirm,
        isValid: dialogInput.length > 0,
      };
    }
    return { isValid: false };
  };
  
  const dialogDetails = getDialogDetails();
  
  return (
    <Container maxWidth="md" className="py-6">
      <Typography variant="h4" className="mb-4 font-weight-bold">
        {translation.security_settings_title}
      </Typography>

      <Paper className="p-4 mb-6 rounded-lg shadow-lg">
        <Typography variant="h5" className="mb-3 font-weight-medium">
          {translation.two_factor_auth}
        </Typography>
        
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Typography variant="body1">
              {translation.two_fa_description}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} className="text-right">
            <FormControlLabel
              control={
                <div className="m-2">
                  <Switch
                    checked={data.is2FAEnabled}
                    onChange={handleToggleChange} 
                    name="2fa_toggle"
                    color="primary"
                    disabled={isUpdating}
                    className="switch-medium toggle-switch-first"
                  />
                </div>
              }
              label={data.is2FAEnabled ? translation.enabled : translation.disabled}
            />
          </Grid>
        </Grid>
        
        {data.is2FAEnabled && data.qrImage && (
          <Box className="mt-4 p-4 border rounded-lg bg-gray-50">
            <Typography variant="h6" className="mb-3 font-weight-semibold text-primary-600">
              {translation.share_two_fa_details}
            </Typography>
            <Typography variant="body2" className="mb-4 text-red-500">
              {translation.shared_account_warning}
            </Typography>

            <Grid container spacing={4} alignItems="center">
              <Grid item xs={12} sm={6} className="text-center">
                <Typography variant="subtitle1" className="font-weight-bold mb-2">
                  {translation.qr_code}
                </Typography>
                <div className="flex justify-center">
                  <img 
                    src={data.qrImage} 
                    alt={translation.qr_code} 
                    style={{ width: '200px', height: '200px', border: '1px solid #ddd' }}
                  />
                </div>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle1" className="font-weight-bold mb-2">
                  {translation.secret_key}
                </Typography>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={data.secret}
                  InputProps={{ readOnly: true }}
                  className="mb-3"
                />
                
                <Button
                  onClick={() => handleOpenDialog('GENERATE')}
                  variant="contained"
                  color="secondary"
                  fullWidth
                  disabled={isUpdating}
                  startIcon={isUpdating ? <CircularProgress size={20} /> : null}
                >
                  {translation.regenerate_secret}
                </Button>
                <Typography variant="caption" className="block mt-2 text-gray-500">
                  {translation.regenerate_secret_note}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        )}
        
        {!data.is2FAEnabled && (
          <Box className="mt-4 p-4 border border-blue-400 rounded-lg bg-blue-50 text-center">
            <Typography>{translation.is_two_fa_disabled}</Typography>
            <Button 
              onClick={() => handleOpenDialog('GENERATE')}
              variant="contained" 
              color="primary" 
              className="mt-3"
              disabled={isUpdating}
            >
              {translation.enable}
            </Button>
          </Box>
        )}
      </Paper>
      
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
        <DialogTitle>{dialogDetails.title}</DialogTitle>
        <DialogContent>
          <Typography className="mb-3">
            {dialogDetails.prompt}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label={dialogDetails.label}
            type={dialogDetails.type}
            fullWidth
            variant="outlined"
            value={dialogInput}
            onChange={(e) => {
              const value = e.target.value;
              if (dialogAction === 'DISABLE') {
                setDialogInput(value.replace(/\D/g, "").slice(0, dialogDetails.maxLength));
              } else {
                setDialogInput(value.slice(0, dialogDetails.maxLength));
              }
            }}
            inputProps={{ maxLength: dialogDetails.maxLength }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} color="default">
            {translation.cancel}
          </Button>
          <Button
            onClick={handleDialogSubmit}
            color="primary"
            disabled={isUpdating || !dialogDetails.isValid}
          >
            {isUpdating ? <CircularProgress size={20} /> : dialogDetails.confirmLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
